import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import type { Message } from '../types';

export const useMessages = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('messages_queue')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      setMessages(data || []);
    }
    setLoading(false);
  };

  const createMessage = async (messageData: Omit<Message, 'id' | 'created_at' | 'status' | 'attempts' | 'user_id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const newMessage = {
      ...messageData,
      user_id: user.id,
      status: 'pending' as const,
      attempts: 0,
    };
    
    const { data, error } = await supabase
      .from('messages_queue')
      .insert([newMessage])
      .select()
      .single();

    if (error) throw error;
    setMessages(prev => [data, ...prev]);
    return data;
  };

  const updateMessage = async (id: string, updates: Partial<Message>) => {
    const { data, error } = await supabase
      .from('messages_queue')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    setMessages(prev => prev.map(m => m.id === id ? data : m));
    return data;
  };

  const deleteMessage = async (id: string) => {
    const { error } = await supabase
      .from('messages_queue')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setMessages(prev => prev.filter(m => m.id !== id));
  };

  const getPendingMessages = () => {
    return messages.filter(m => m.status === 'pending' || m.status === 'failed');
  };

  const getPendingCount = () => {
    return messages.filter(m => m.status === 'pending').length;
  };

  const retryMessage = async (id: string) => {
    return updateMessage(id, { status: 'pending', error: undefined });
  };

  return {
    messages,
    loading,
    createMessage,
    updateMessage,
    deleteMessage,
    fetchMessages,
    getPendingMessages,
    getPendingCount,
    retryMessage,
  };
};
